import React, { ReactNode, useState } from 'react'
import { InferType } from 'yup'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation, useQueries } from '@tanstack/react-query'
import { SocietyWebsiteBase } from '../SocietyWebsiteBase'
import { societyWebsiteContactSchema } from '../../../../forms/schemas/society_website_contact'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { Toggle } from '../../../common/Toggle'
import { FormControl } from '../../../common/FormControl'
import { SelectDropdown } from '../../../common/SelectDropdown'
import { Icon, IconChoices } from '../../../common/Icon'
import { Button, ButtonVariant } from '../../../common/Button'
import { TextInput } from '../../../common/TextInput'
import { ConfirmationModal } from '../../../common/ConfirmationModal'
import { useModal } from '../../../../hooks/useModal'
import { theme } from '../../../../theme/theme'
import { TextTooltip } from '../../../common/Tooltip'
import { ISocietyWebsiteModel } from '../../../../interfaces/models/societies-websites.interfaces'
import { userQueries } from '../../../../api/users'
import { patchWebsite } from '../../../../api/society-websites'
import { getUserFullName } from '../../../../helpers/user'
import { i18nSocietyType } from '../../../../i18n'

interface SocietyWebsiteContactProps {
  website: ISocietyWebsiteModel
}

export const SocietyWebsiteContact = ({
  website,
}: SocietyWebsiteContactProps): ReactNode => {
  const [selectedDeleteIndex, setSelectedDeleteIndex] = useState(0)
  const {
    show: showConfirmationModal,
    open: openConfirmationModal,
    close: closeConfirmationModal,
  } = useModal()
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { getErrorMessage } = useFormErrorMessage()
  const { society } = useCurrentSociety()
  const [referenceElement, setReferenceElement] = useState<Element | null>(null)

  const recipientEmails =
    website.contact?.recipientsCC?.flatMap((cc) => cc.userEmail || []) || []
  const recipientUserIds = website.contact?.recipientsCC
    ?.filter((cc) => cc.userId)
    .map((cc) => cc.userId)
  const usersQuery = useQueries({
    queries:
      recipientUserIds?.map((userId) => userQueries.getUser(userId)) || [],
  })
  const userEmails = usersQuery.flatMap((query) => query.data?.email || [])

  const getDefaultValues = (): InferType<
    typeof societyWebsiteContactSchema
  > => {
    return {
      show: website.contact?.show !== undefined ? website.contact.show : false,
      recipientId: website.contact?.recipientId || undefined,
      recipientsCC: [...recipientEmails, ...userEmails].map((email) => ({
        userEmail: email,
      })),
    }
  }

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyWebsiteContactSchema),
    defaultValues: getDefaultValues(),
  })

  const { fields, append, remove } = useFieldArray({
    name: 'recipientsCC',
    control,
  })

  const { mutate: mutatePatchWebsite, isPending: isUpdatingWebsite } =
    useMutation({
      mutationFn: patchWebsite,
      onSuccess: () => {
        setToastNotification(
          ToastType.SUCCESS,
          translate('societyWebsiteContact.flashMessage.updateSuccess')
        )
      },
      onError: () => {
        setToastNotification(
          ToastType.DANGER,
          translate('societyWebsiteContact.flashMessage.updateFailure')
        )
      },
    })

  const onSubmit = async (
    data: InferType<typeof societyWebsiteContactSchema>
  ): Promise<void> => {
    let { recipientId } = data
    if (!recipientId || recipientId === '') {
      recipientId = undefined
    }
    mutatePatchWebsite({
      id: website._id,
      body: {
        contact: { ...data, recipientId },
      },
    })
  }

  const onError = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('societyWebsiteContact.flashMessage.updateFailure')
    )
  }

  const addNewEmail = (): void => {
    append({ userEmail: '' }, { shouldFocus: true })
  }

  const openDeleteConfirmationModal = (index: number): void => {
    setSelectedDeleteIndex(index)
    openConfirmationModal()
  }

  const deleteCC = (): void => {
    remove(selectedDeleteIndex)
  }

  const boardMembersList = society?.boardMembersList || []
  const boardMembersQuery = useQueries({
    queries:
      boardMembersList?.map((board) => userQueries.getUser(board.userId)) || [],
  })

  const boardMembers = boardMembersQuery.flatMap((query) =>
    query.data
      ? { label: getUserFullName(query.data), value: query.data._id }
      : []
  )

  const loading = isUpdatingWebsite
  return (
    <form
      className="w-full max-w-3xl"
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <SocietyWebsiteBase website={website} isValid={isValid} loading={loading}>
        <div className="max-w-screen-sm space-y-6">
          <Controller
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <Toggle
                label={translate('societyWebsite.showOnWebsite')}
                name={name}
                enabled={value || false}
                onChange={onChange}
              />
            )}
            name="show"
          />
          <div className="space-y-4">
            <p style={theme.textVariants.base}>
              {translate('societyWebsiteContact.form.recipientLabel')}
            </p>
            <Controller
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <FormControl
                  label={translate('societyWebsiteContact.form.recipientId', {
                    context: i18nSocietyType(society?.societyTypeDisplayName),
                  })}
                  name={name}
                  error={
                    errors.recipientId && getErrorMessage(errors.recipientId)
                  }
                >
                  <SelectDropdown
                    isClearable
                    options={boardMembers}
                    value={boardMembers.find(
                      (option) => option.value === value
                    )}
                    onChange={(option) => onChange(option?.value ?? '')}
                    error={!!errors.recipientId}
                  />
                </FormControl>
              )}
              name="recipientId"
            />
          </div>
          <div className="space-y-4">
            <div className="flex space-x-4">
              <p style={theme.textVariants.base}>
                {translate('societyWebsiteContact.form.copy')}
              </p>
              <TextTooltip
                title={translate('societyWebsiteContact.form.copyTooltip')}
                referenceElement={referenceElement}
              >
                <Icon
                  icon={IconChoices.QUESTION_MARK}
                  ref={setReferenceElement}
                />
              </TextTooltip>
            </div>
            {fields.map((field, index) => (
              <>
                <Controller
                  control={control}
                  key={field.id}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <FormControl
                      className="w-full"
                      error={
                        errors.recipientsCC?.[index]?.userEmail?.type ===
                        'required'
                          ? translate('form.errors.required')
                          : ''
                      }
                      name={`recipientsCC.${index}.userEmail`}
                    >
                      <TextInput
                        className="w-full"
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        error={!!errors.recipientsCC?.[index]?.userEmail}
                        onDeleteIconPress={() =>
                          openDeleteConfirmationModal(index)
                        }
                        type="email"
                      />
                    </FormControl>
                  )}
                  name={`recipientsCC.${index}.userEmail`}
                />
              </>
            ))}
          </div>
          <Button
            icon={IconChoices.PLUS_SIGN}
            label={translate('societyWebsiteContact.form.addMore')}
            variant={ButtonVariant.DEFAULT}
            onClick={addNewEmail}
          />
        </div>
      </SocietyWebsiteBase>
      {showConfirmationModal && (
        <ConfirmationModal
          title={translate(
            'societyWebsiteContact.confirmationModal.removeEmailTitle'
          )}
          description={translate(
            'societyWebsiteContact.confirmationModal.removeEmailText'
          )}
          show={showConfirmationModal}
          close={closeConfirmationModal}
          onConfirm={() => deleteCC()}
          deleteMode
        />
      )}
    </form>
  )
}
